import { useRef, useState } from 'react';
import styles from './css/ibpt-import-modal.module.css';
import { importIbptTable } from '../../services/ibpt/ibpt.service';

const UF_OPTIONS = [
    'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO',
    'MA', 'MG', 'MS', 'MT', 'PA', 'PB', 'PE', 'PI', 'PR',
    'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO',
];

function formatBytes(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function IbptImportModal({ isOpen, onClose }) {
    const inputRef = useRef(null);
    const [uf, setUf] = useState('');
    const [file, setFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    if (!isOpen) return null;

    function handleFileChange(e) {
        const selected = e.target.files?.[0] ?? null;
        setFile(selected);
        setResult(null);
        setError(null);
    }

    function handleRemoveFile() {
        setFile(null);
        setResult(null);
        setError(null);
        if (inputRef.current) inputRef.current.value = '';
    }

    async function handleImport() {
        if (!uf || !file) return;
        setIsLoading(true);
        setError(null);
        setResult(null);

        try {
            const formData = new FormData();
            formData.append('uf', uf);
            formData.append('file', file);

            const data = await importIbptTable(formData);

            if (data.erros > 0 && data.inseridos === 0) {
                throw new Error(data.mensagensErro?.[0] ?? 'Erro ao importar o arquivo.');
            }

            setResult(data);
            setFile(null);
            if (inputRef.current) inputRef.current.value = '';
        } catch (err) {
            setError(err.message ?? 'Erro inesperado ao importar a tabela.');
        } finally {
            setIsLoading(false);
        }
    }

    function handleClose() {
        if (isLoading) return;
        setUf('');
        setFile(null);
        setResult(null);
        setError(null);
        if (inputRef.current) inputRef.current.value = '';
        onClose();
    }

    return (
        <div className={styles.overlay} onClick={handleClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>

                {/* HEADER */}
                <div className={styles.header}>
                    <h2 className={styles.title}>Importar Tabela IBPT</h2>
                    <button className={styles.closeBtn} onClick={handleClose} disabled={isLoading}>✕</button>
                </div>

                <p className={styles.description}>
                    Selecione a UF e o arquivo CSV da tabela IBPT correspondente.
                    Os dados anteriores da UF selecionada serão substituídos.
                </p>

                {/* SELECT DE UF */}
                <div className={styles.field}>
                    <label className={styles.label}>Estado (UF)</label>
                    <select
                        className={styles.select}
                        value={uf}
                        onChange={(e) => { setUf(e.target.value); setResult(null); setError(null); }}
                        disabled={isLoading}
                    >
                        <option value=''>Selecione a UF...</option>
                        {UF_OPTIONS.map((u) => (
                            <option key={u} value={u}>{u}</option>
                        ))}
                    </select>
                </div>

                {/* DROPZONE / FILE INPUT */}
                <div
                    className={`${styles.dropzone} ${isLoading ? styles.dropzoneDisabled : ''}`}
                    onClick={() => !isLoading && inputRef.current?.click()}
                >
                    <span className={styles.dropzoneIcon}>📂</span>
                    <p className={styles.dropzoneText}>
                        Clique para selecionar o arquivo <strong>.csv</strong>
                    </p>
                    <p className={styles.dropzoneHint}>Máx. 5 MB</p>
                    <input
                        ref={inputRef}
                        type='file'
                        accept='.csv'
                        className={styles.hiddenInput}
                        disabled={isLoading}
                        onChange={handleFileChange}
                    />
                </div>

                {/* ARQUIVO SELECIONADO */}
                {file && (
                    <div className={styles.fileItem}>
                        <span className={styles.fileName}>{file.name}</span>
                        <span className={styles.fileSize}>{formatBytes(file.size)}</span>
                        {!isLoading && (
                            <button className={styles.removeBtn} onClick={handleRemoveFile}>✕</button>
                        )}
                    </div>
                )}

                {/* ERRO */}
                {error && (
                    <p className={styles.errorMessage}>{error}</p>
                )}

                {/* RESULTADO */}
                {result && (
                    <div className={styles.resultTable}>
                        <table>
                            <thead>
                                <tr>
                                    {['Arquivo', 'UF', 'Total', 'Inseridos', 'Erros'].map((col) => (
                                        <th key={col}>{col}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className={styles.monoCell}>{result.arquivo}</td>
                                    <td>{result.uf}</td>
                                    <td>{result.totalLinhas?.toLocaleString('pt-BR') ?? '-'}</td>
                                    <td className={styles.successCell}>{result.inseridos?.toLocaleString('pt-BR') ?? '-'}</td>
                                    <td className={styles.errorCell}>{result.erros?.toLocaleString('pt-BR') ?? '-'}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}

                {/* FOOTER */}
                <div className={styles.footer}>
                    <button className={styles.cancelBtn} onClick={handleClose} disabled={isLoading}>
                        Fechar
                    </button>
                    <button
                        className={styles.importBtn}
                        onClick={handleImport}
                        disabled={!uf || !file || isLoading}
                    >
                        {isLoading ? 'Importando...' : 'Importar'}
                    </button>
                </div>
            </div>
        </div>
    );
}
